import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Clock, Loader2, ArrowLeft, MessageCircle } from "lucide-react";
import { api, apiError, formatCOP } from "@/lib/api";
import { RAFFLE, whatsappLink } from "@/lib/raffleData";

const MAX_TRIES = 60; // ~ 5 minutes @ 5s
const POLL_MS = 5000;

export default function PaymentResult() {
  const [params] = useSearchParams();
  const txId = params.get("id");
  const [status, setStatus] = useState("PENDING");
  const [data, setData] = useState(null);
  const [tries, setTries] = useState(0);
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    if (!txId) {
      setStatus("ERROR");
      setError("No se recibió el ID de la transacción");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data: res } = await api.get(`/wompi/status/${encodeURIComponent(txId)}`);
        if (cancelled) return;
        setData(res);
        setStatus(res.status || "PENDING");
        if (res.status === "PENDING") {
          setTries((t) => {
            if (t + 1 >= MAX_TRIES) {
              setStatus("TIMEOUT");
              return t + 1;
            }
            timer.current = setTimeout(poll, POLL_MS);
            return t + 1;
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError(apiError(e));
          setStatus("ERROR");
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [txId]);

  const isApproved = status === "APPROVED";
  const isPending = status === "PENDING";
  const isFailed = ["DECLINED", "VOIDED", "ERROR", "TIMEOUT"].includes(status);

  return (
    <div className="min-h-screen bg-[#051A0F] grain-overlay flex items-center justify-center px-4 py-16" data-testid="payment-result">
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg rounded-3xl border-2 border-[#F0B91B]/40 bg-[#0A2818] p-8 md:p-10 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.95)]"
      >
        <div className="text-center">
          {isPending && (
            <>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#F0B91B]/15 border-2 border-[#F0B91B]/40 mb-4">
                <Loader2 className="w-10 h-10 text-[#F0B91B] animate-spin" />
              </div>
              <h1 className="font-bebas text-4xl text-[#FDFBF7] tracking-wider">Procesando pago…</h1>
              <p className="text-sm text-[#FDFBF7]/60 font-outfit mt-2">Estamos verificando tu pago con Wompi. Esto suele tardar unos segundos.</p>
            </>
          )}
          {isApproved && (
            <>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#7FD1AE]/15 border-2 border-[#7FD1AE]/50 mb-4">
                <CheckCircle2 className="w-10 h-10 text-[#7FD1AE]" />
              </div>
              <h1 className="font-bebas text-4xl text-[#FDFBF7] tracking-wider">¡Pago aprobado!</h1>
              <p className="text-sm text-[#FDFBF7]/70 font-outfit mt-2">
                Tu ficha <span className="text-[#F0B91B] font-bold">#{data?.ficha_number}</span> ha sido registrada.
              </p>
              <div className="mt-6 rounded-xl bg-[#051A0F] border border-[#F0B91B]/25 p-4 text-left text-sm font-outfit text-[#FDFBF7]/85 space-y-1.5" data-testid="payment-result-success">
                <div><span className="text-[#F0B91B]">Comprador:</span> {data?.buyer_name}</div>
                <div><span className="text-[#F0B91B]">Ficha:</span> #{data?.ficha_number}</div>
                <div><span className="text-[#F0B91B]">Monto:</span> {formatCOP((data?.amount_in_cents || 0) / 100)}</div>
                <div><span className="text-[#F0B91B]">Referencia:</span> <span className="text-xs">{data?.reference}</span></div>
              </div>
              <a
                href={whatsappLink(`Hola BETMAR, mi pago Wompi fue aprobado. Ficha #${data?.ficha_number}. Referencia: ${data?.reference}. ¡Quiero mi comprobante!`)}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white py-3.5 rounded-xl font-bebas text-lg md:text-xl tracking-wider shadow-[0_15px_40px_-10px_rgba(37,211,102,0.7)] transition-all"
                data-testid="payment-result-wa"
              >
                <MessageCircle className="w-5 h-5" />
                Recibir comprobante por WhatsApp
              </a>
            </>
          )}
          {isFailed && (
            <>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E63946]/15 border-2 border-[#E63946]/50 mb-4">
                {status === "TIMEOUT" ? <Clock className="w-10 h-10 text-[#E63946]" /> : <XCircle className="w-10 h-10 text-[#E63946]" />}
              </div>
              <h1 className="font-bebas text-4xl text-[#FDFBF7] tracking-wider">
                {status === "DECLINED" && "Pago rechazado"}
                {status === "VOIDED" && "Pago cancelado"}
                {status === "TIMEOUT" && "Verificación lenta"}
                {(status === "ERROR" || !status) && "Ocurrió un error"}
              </h1>
              <p className="text-sm text-[#FDFBF7]/70 font-outfit mt-2">
                {status === "TIMEOUT"
                  ? "No pudimos confirmar el pago en este momento. Si ya pagaste, contáctanos por WhatsApp y verificaremos manualmente."
                  : "Tu ficha sigue disponible. Puedes intentarlo nuevamente o pagar por WhatsApp."}
              </p>
              {error && <p className="text-xs text-[#E63946]/80 mt-2">{error}</p>}
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/#elegir-ficha"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] py-3.5 rounded-xl font-bebas text-lg tracking-wider transition-all"
                  data-testid="payment-result-retry"
                >
                  Intentar de nuevo
                </Link>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white py-3.5 rounded-xl font-bebas text-lg tracking-wider transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </>
          )}
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#FDFBF7]/50 hover:text-[#F0B91B] font-outfit transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Volver al inicio
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
